
import React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ChangePlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?: string;
  billingPeriod: 'monthly' | 'yearly';
}

const ChangePlanDialog = ({ isOpen, onClose, currentPlan, billingPeriod }: ChangePlanDialogProps) => {
  const [selectedPlan, setSelectedPlan] = React.useState<'standard' | 'pro'>(
    currentPlan?.toLowerCase() === 'pro' ? 'pro' : 'standard'
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const { toast } = useToast();

  const handleChangePlan = async () => {
    if (selectedPlan === currentPlan?.toLowerCase()) {
      onClose();
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          domain: window.location.hostname,
          billingPeriod,
          planType: selectedPlan,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Change plan error:', error);
      toast({
        title: "Error",
        description: "Could not process plan change. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Change Subscription Plan</AlertDialogTitle>
          <AlertDialogDescription>
            Choose your new plan. You'll be redirected to complete the change.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4">
          <RadioGroup
            value={selectedPlan}
            onValueChange={(value: 'standard' | 'pro') => setSelectedPlan(value)}
            className="grid gap-4"
          >
            <div className="flex items-start space-x-3 space-y-0">
              <RadioGroupItem value="standard" id="standard" className="mt-1" />
              <div className="grid gap-1.5">
                <Label htmlFor="standard" className="font-medium">
                  Standard Plan
                </Label>
                <p className="text-sm text-muted-foreground">
                  {billingPeriod === 'monthly' ? 'AUD$1.99/month' : 'AUD$19.99/year'}
                  <br />
                  Perfect for a single website installation
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 space-y-0">
              <RadioGroupItem value="pro" id="pro" className="mt-1" />
              <div className="grid gap-1.5">
                <Label htmlFor="pro" className="font-medium">
                  Pro Plan
                </Label>
                <p className="text-sm text-muted-foreground">
                  {billingPeriod === 'monthly' ? 'AUD$9.99/month' : 'AUD$99.99/year'}
                  <br />
                  Unlimited website installations
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button 
            onClick={handleChangePlan} 
            disabled={isLoading || selectedPlan === currentPlan?.toLowerCase()}
          >
            {isLoading ? "Processing..." : "Continue"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ChangePlanDialog;
